// pages/SearchResults.js
import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import PropertyCard from '../components/PropertyCard';
import './SearchResults.css';

// Iconos para el buscador
const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
  </svg>
);

const FilterIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"/>
  </svg>
);

const SearchResults = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useState({});
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    type: 'all',
    bedrooms: 'all',
    priceRange: 'all'
  });

  // Datos reales de los 3 apartamentos
  const allProperties = [
    {
      id: 'apartamento-401',
      title: 'Apartamento 401',
      location: 'Barranquilla, Atlántico',
      price: '$95,000',
      type: 'Apartamento',
      bedrooms: 1,
      bathrooms: 1,
      area: '50 m²',
      image: '/images/401/1.jpeg',
      description: 'Apartamento 401 de 50 metros cuadrados con sala comedor, cocina, área de labor aparte, cuarto, baño y clóset. Completamente amoblado con servicios al día, internet y cable.'
    },
    {
      id: 'apartamento-602',
      title: 'Apartamento 602',
      location: 'Barranquilla, Atlántico',
      price: '$85,000',
      type: 'Apartamento',
      bedrooms: 1,
      bathrooms: 1,
      area: '41 m²',
      image: '/images/602/1.jpeg',
      description: 'Apartamento 602 de 41 metros cuadrados con sala, cocina, pequeño espacio para área de labor, cuarto, baño y clóset. Completamente amoblado con servicios al día, internet y cable.'
    },
    {
      id: 'apartamento-702',
      title: 'Apartamento 702',
      location: 'Barranquilla, Atlántico',
      price: '$85,000',
      type: 'Apartamento',
      bedrooms: 1,
      bathrooms: 1,
      area: '41 m²',
      image: '/images/702/1.jpeg',
      description: 'Apartamento 702 de 41 metros cuadrados con sala, cocina, pequeño espacio para área de labor, cuarto, baño y clóset. Completamente amoblado con servicios al día, internet y cable.'
    }
  ];

  useEffect(() => {
    // Parsear parámetros de búsqueda de la URL
    const params = new URLSearchParams(location.search);
    const searchData = {
      q: params.get('q') || '',
      type: params.get('type') || 'ciudad'
    };
    setSearchParams(searchData);
    
    // Simular carga de datos
    setTimeout(() => {
      setProperties(allProperties);
      setLoading(false);
    }, 500);
  }, [location]);

  useEffect(() => {
    // Filtrar propiedades basado en la búsqueda y filtros
    if (properties.length > 0) {
      let results = properties;

      // Aplicar búsqueda principal si existe
      if (searchParams.q) {
        const searchTerm = searchParams.q.toLowerCase().trim();
        
        results = results.filter(property => {
          switch (searchParams.type) {
            case 'ciudad':
              return property.location.toLowerCase().includes(searchTerm);
            case 'nombre':
              return property.title.toLowerCase().includes(searchTerm) ||
                     property.description.toLowerCase().includes(searchTerm);
            case 'precio':
              // Buscar por rango de precio o precio exacto
              if (searchTerm.includes('-')) {
                // Búsqueda por rango
                const [min, max] = searchTerm.split('-').map(p => parseInt(p.replace(/[$,]/g, '')));
                const propertyPrice = parseInt(property.price.replace(/[$,]/g, ''));
                return propertyPrice >= min && propertyPrice <= max;
              } else {
                // Búsqueda por precio exacto o aproximado
                const searchPrice = parseInt(searchTerm.replace(/[$,]/g, ''));
                const propertyPrice = parseInt(property.price.replace(/[$,]/g, ''));
                return propertyPrice <= searchPrice || 
                       property.price.toLowerCase().includes(searchTerm);
              }
            default:
              return property.title.toLowerCase().includes(searchTerm) ||
                     property.location.toLowerCase().includes(searchTerm) ||
                     property.description.toLowerCase().includes(searchTerm);
          }
        });
      }

      // Aplicar filtros adicionales
      if (filters.type !== 'all') {
        results = results.filter(property => property.type === filters.type);
      }

      if (filters.bedrooms !== 'all') {
        results = results.filter(property => property.bedrooms >= parseInt(filters.bedrooms));
      }

      if (filters.priceRange !== 'all') {
        results = results.filter(property => {
          const price = parseInt(property.price.replace(/[$,]/g, ''));
          switch (filters.priceRange) {
            case '80000-90000':
              return price >= 80000 && price <= 90000;
            case '90000-100000':
              return price >= 90000 && price <= 100000;
            case '100000+':
              return price > 100000;
            default:
              return true;
          }
        });
      }

      setFilteredProperties(results);
    } else {
      setFilteredProperties(properties);
    }
  }, [properties, searchParams, filters]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      type: 'all',
      bedrooms: 'all',
      priceRange: 'all'
    });
  };

  const getSearchTypeLabel = () => {
    switch (searchParams.type) {
      case 'ciudad':
        return 'ubicación';
      case 'nombre':
        return 'nombre';
      case 'precio':
        return 'precio';
      default:
        return 'término';
    }
  };

  if (loading) {
    return (
      <div className="search-results">
        <div className="container">
          <div className="loading">Buscando propiedades...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="search-results">
      <div className="container">
        {/* Header de resultados */}
        <div className="results-header">
          <div className="search-info">
            <h1>
              {searchParams.q ? `Resultados para "${searchParams.q}"` : 'Todos nuestros apartamentos'}
            </h1>
            <p>
              {filteredProperties.length} propiedad{filteredProperties.length !== 1 ? 'es' : ''} encontrada{filteredProperties.length !== 1 ? 's' : ''}
              {searchParams.q && ` por ${getSearchTypeLabel()}`}
              {searchParams.q && searchParams.type === 'precio' && ' en Barranquilla'}
            </p>
          </div>
          
          {/* Filtros rápidos */}
          <div className="quick-filters">
            <button 
              className={`filter-btn ${filters.type === 'all' ? 'active' : ''}`}
              onClick={() => handleFilterChange('type', 'all')}
            >
              Todos
            </button>
            <button 
              className={`filter-btn ${filters.bedrooms === '1' ? 'active' : ''}`}
              onClick={() => handleFilterChange('bedrooms', '1')}
            >
              1 Habitación
            </button>
            <button 
              className={`filter-btn ${filters.priceRange === '80000-90000' ? 'active' : ''}`}
              onClick={() => handleFilterChange('priceRange', '80000-90000')}
            >
              $80,000-90,000
            </button>
          </div>
        </div>

        <div className="results-content">
          {/* Sidebar de filtros */}
          <div className="filters-sidebar">
            <div className="filters-header">
              <FilterIcon />
              <h3>Filtros</h3>
              <button onClick={clearFilters} className="clear-filters">
                Limpiar
              </button>
            </div>

            {/* Filtro por tipo */}
            <div className="filter-group">
              <label>Tipo de propiedad</label>
              <select 
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
              >
                <option value="all">Todos los tipos</option>
                <option value="Apartamento">Apartamento</option>
              </select>
            </div>

            {/* Filtro por habitaciones */}
            <div className="filter-group">
              <label>Habitaciones</label>
              <select 
                value={filters.bedrooms}
                onChange={(e) => handleFilterChange('bedrooms', e.target.value)}
              >
                <option value="all">Cualquier cantidad</option>
                <option value="1">1 habitación</option>
              </select>
            </div>

            {/* Filtro por precio */}
            <div className="filter-group">
              <label>Rango de precio</label>
              <select 
                value={filters.priceRange}
                onChange={(e) => handleFilterChange('priceRange', e.target.value)}
              >
                <option value="all">Cualquier precio</option>
                <option value="80000-90000">$80,000 - $90,000</option>
                <option value="90000-100000">$90,000 - $100,000</option>
                <option value="100000+">Más de $100,000</option>
              </select>
            </div>

            {/* Información de ubicación */}
            <div className="location-info">
              <h4>📍 Ubicación</h4>
              <p>Todos nuestros apartamentos están ubicados en:</p>
              <p className="address">Carrera 58 # 82-41<br/>Edificio K58<br/>Barranquilla, Colombia</p>
            </div>
          </div>

          {/* Resultados */}
          <div className="results-grid">
            {filteredProperties.length > 0 ? (
              <div className="properties-grid">
                {filteredProperties.map(property => (
                  <PropertyCard key={property.id} property={property} />
                ))}
              </div>
            ) : (
              <div className="no-results">
                <div className="no-results-content">
                  <SearchIcon />
                  <h3>No se encontraron propiedades</h3>
                  <p>
                    {searchParams.q 
                      ? `No hay propiedades que coincidan con "${searchParams.q}" en ${getSearchTypeLabel()}`
                      : 'No hay propiedades disponibles con los filtros seleccionados'
                    }
                  </p>
                  <div className="suggestions">
                    <p>Prueba con:</p>
                    <ul>
                      <li>Buscar por "Barranquilla" en ciudad</li>
                      <li>Buscar por "401", "602" o "702" en nombre</li>
                      <li>Buscar por "$85,000" o "$95,000" en precio</li>
                    </ul>
                  </div>
                  <Link to="/" className="back-to-home">
                    Volver al inicio
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchResults;